import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

import { texteTheme } from '../utils/theme';
import FactureDetailModal from './shared/FactureDetailModal';
function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = (typeof d === 'string' ? d : '').split('T')[0].split('-').map(Number);
  return (y && m && day) ? new Date(y, m - 1, day).toLocaleDateString('fr-FR') : '—';
}

function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
  }).format(m);
}

function formatMontantInt(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(m);
}

export default function FacturesServices({ user }) {
  const [factures, setFactures]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [syncMsg, setSyncMsg]         = useState('');
  const [syncError, setSyncError]     = useState('');
  const pollRef                       = useRef(null);
  const [search, setSearch]           = useState('');
  const [filterDept, setFilterDept]   = useState('');
  const [dateDebut, setDateDebut]     = useState('');
  const [dateFin, setDateFin]         = useState('');
  const [selected, setSelected]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)     params.search     = search;
      if (filterDept) params.department = filterDept;
      if (dateDebut)  params.date_debut = dateDebut;
      if (dateFin)    params.date_fin   = dateFin;

      const r = await api.get('/factures-services', { params });
      setFactures(r.data.factures || []);
    } catch (err) {
      console.error('Erreur chargement factures services:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterDept, dateDebut, dateFin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/factures-services/departments')
      .then(r => setDepartments(r.data.departments || []))
      .catch(() => {});
    api.get('/factures-services/stats')
      .then(r => setStats(r.data.stats))
      .catch(() => {});
  }, []);

  // Arrêter le polling quand le composant se démonte
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    setSyncError('');
    setSyncProgress('Connexion SFTP…');
    try {
      // Lancer la sync (réponse immédiate)
      await api.post('/factures-services/sync');

      // Poller /sync-status toutes les 3 secondes
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get('/factures-services/sync-status');
          const state = s.data;
          setSyncProgress(state.progress || '');

          if (!state.running) {
            stopPolling();
            setSyncing(false);
            setSyncProgress('');
            if (state.error) {
              setSyncError(state.error);
            } else {
              if (state.result) setSyncMsg(state.result.message || 'Synchronisation terminée');
              await load();
              const sr = await api.get('/factures-services/stats');
              setStats(sr.data.stats);
              const dr = await api.get('/factures-services/departments');
              setDepartments(dr.data.departments || []);
            }
          }
        } catch {
          // Erreur réseau passagère, on continue
        }
      }, 3000);

    } catch (err) {
      stopPolling();
      setSyncing(false);
      setSyncProgress('');
      setSyncError(err.response?.data?.error || 'Erreur lors du lancement de la synchronisation');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div style={{ padding: '16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>
          🧾 Factures Services
        </h2>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '7px 16px', background: syncing ? '#9ca3af' : '#0ea5e9',
            color: '#fff', border: 'none', borderRadius: '6px',
            fontSize: '13px', fontWeight: 600, cursor: syncing ? 'default' : 'pointer',
          }}
        >
          {syncing ? '⏳ Synchronisation…' : '🔄 Sync SFTP (Ateliers.all.csv)'}
        </button>
      </div>

      {/* Feedback sync */}
      {syncing && syncProgress && (
        <div style={{ padding: '10px 14px', background: 'var(--color-info-light)', color: 'var(--color-info-dark)', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
          {syncProgress}
          <span style={{ fontSize: '11px', color: 'light-dark(#3b82f6, var(--color-info-dark))', marginLeft: 'auto' }}>Le fichier est volumineux (~220 Mo), la sync peut prendre plusieurs minutes…</span>
        </div>
      )}
      {syncMsg && (
        <div style={{ padding: '10px 14px', background: 'var(--color-success-light)', color: 'var(--color-success-dark)', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
          ✅ {syncMsg}
        </div>
      )}
      {syncError && (
        <div style={{ padding: '10px 14px', background: 'var(--color-error-light)', color: 'var(--color-error-dark)', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
          ❌ {syncError}
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total factures', value: parseInt(stats.total).toLocaleString('fr-FR'), color: '#3b82f6' },
            { label: 'Ce mois', value: parseInt(stats.ce_mois).toLocaleString('fr-FR'), color: '#8b5cf6' },
            { label: 'Montant total', value: formatMontantInt(stats.montant_total), color: '#059669' },
            { label: 'Montant ce mois', value: formatMontantInt(stats.montant_mois), color: '#f59e0b' },
          ].map(kpi => (
            <div key={kpi.label} style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '8px',
              padding: '12px 16px', minWidth: '140px', flex: 1,
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{kpi.label}</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: texteTheme(kpi.color) }}>{kpi.value}</div>
            </div>
          ))}
          {stats.derniere_sync && (
            <div style={{
              background: 'light-dark(#f9fafb, var(--bg-muted))', border: '1px solid var(--border-default)', borderRadius: '8px',
              padding: '12px 16px', minWidth: '160px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Dernière sync</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                {new Date(stats.derniere_sync).toLocaleString('fr-FR')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtres */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input
          type="text"
          placeholder="🔍 Facture, OR, client, immat…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '7px 10px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
        />
        <select
          value={filterDept}
          onChange={e => setFilterDept(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
        >
          <option value="">Tous les départements</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input
          type="date" value={dateDebut}
          onChange={e => setDateDebut(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
        />
        <input
          type="date" value={dateFin}
          onChange={e => setDateFin(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid var(--border-strong)', borderRadius: '6px', fontSize: '13px' }}
        />
        <button
          type="submit"
          style={{ padding: '7px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
        >
          Filtrer
        </button>
        {(search || filterDept || dateDebut || dateFin) && (
          <button type="button" onClick={() => { setSearch(''); setFilterDept(''); setDateDebut(''); setDateFin(''); }}
            style={{ padding: '7px 10px', background: 'var(--border-default)', color: 'var(--text-primary)', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
            ✕ Réinitialiser
          </button>
        )}
      </form>

      {/* Tableau */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'light-dark(#f9fafb, var(--bg-muted))', borderBottom: '2px solid var(--border-default)' }}>
                {['N° Facture', 'Date facture', 'N° OR', 'Client', 'Immat', 'Marque', 'Département', 'Réceptionnaire', 'Montant HT', 'Lignes'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Chargement…</td></tr>
              ) : factures.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  Aucune facture — lancez une synchronisation SFTP
                </td></tr>
              ) : factures.map(f => (
                <tr
                  key={f.id}
                  onClick={() => setSelected(f)}
                  style={{ borderBottom: '1px solid light-dark(#f3f4f6, var(--border-default))', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: 'var(--color-info-dark)', whiteSpace: 'nowrap' }}>{f.num_facture}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{formatDate(f.date_facture)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-primary)' }}>{f.num_or || '—'}</td>
                  <td style={{ padding: '9px 12px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nom_client || '—'}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '12px' }}>{f.immat || '—'}</td>
                  <td style={{ padding: '9px 12px' }}>{f.marque || '—'}</td>
                  <td style={{ padding: '9px 12px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{
                      background: 'var(--color-info-light)', color: 'var(--color-info-dark)',
                      padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    }}>{f.department || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 12px', color: 'var(--text-secondary)' }}>{f.receptionnaire || '—'}</td>
                  <td style={{
                    padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap',
                    color: parseFloat(f.montant_ht) < 0 ? 'light-dark(#dc2626, var(--color-error-dark))' : 'light-dark(#059669, var(--color-success-dark))',
                  }}>
                    {formatMontant(f.montant_ht)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'center', color: 'var(--text-secondary)' }}>{f.nb_lignes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {factures.length > 0 && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-default)', fontSize: '12px', color: 'var(--text-secondary)' }}>
            {factures.length} facture(s) affichée(s)
            {factures.length === 500 && ' — affichage limité à 500 résultats, affinez vos filtres'}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {selected && (
            <FactureDetailModal facture={selected} onClose={() => setSelected(null)} />
          )}
    </div>
  );
}
